import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { 
  QrCode, 
  Copy, 
  Download, 
  Clock, 
  CheckCircle, 
  ArrowLeft, 
  AlertCircle,
  Smartphone,
  Building
} from 'lucide-react'
import { apiService, useApi } from '../services/api'
import logoTander from '../assets/WhatsApp_Image_2025-08-29_at_16.10.10-removebg-preview.png'

const PixPaymentPage = () => {
  console.log('🚀 PixPaymentPage renderizando...')
  
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { checkOrderStatus } = useApi()
  const [isLoading, setIsLoading] = useState(true)
  const [pixData, setPixData] = useState<any>(null)
  const [countdown, setCountdown] = useState(900) // 15 minutos
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid' | 'failed' | 'expired'>('pending')
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false)
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false)
  const [confirmationResult, setConfirmationResult] = useState<any>(null)
  const [redirectCountdown, setRedirectCountdown] = useState(5)
  const intervalRef = useRef<number | null>(null)
  const currentTransactionIdRef = useRef<string | null>(null)
  
  console.log('🔍 Estados iniciais:', {
    isLoading,
    pixData,
    error,
    paymentStatus,
    showSuccessAnimation
  })
  
  // Debug: Verificar se há dados nos searchParams
  console.log('🔍 SearchParams:', {
    plan: searchParams.get('plan'),
    plan_name: searchParams.get('plan_name'),
    price: searchParams.get('price'),
    currency: searchParams.get('currency'),
    qr_code: searchParams.get('qr_code'),
    payment_url: searchParams.get('payment_url'),
    pix_key: searchParams.get('pix_key')
  })

  const planName = searchParams.get('plan_name') || 'Plano Selecionado'
  const price = searchParams.get('price') || '0.00'

  // Countdown
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  // Countdown para redirecionamento após pagamento confirmado
  useEffect(() => {
    if (showSuccessAnimation && redirectCountdown > 0) {
      const timer = setTimeout(() => {
        setRedirectCountdown(redirectCountdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else if (showSuccessAnimation && redirectCountdown === 0) {
      // Redirecionar quando o countdown chegar a 0
      console.log(`🚀 [${new Date().toISOString()}] Redirecionando para https://tander-smart-app.web.app/login`)
      window.location.href = 'https://tander-smart-app.web.app/login'
    }
  }, [showSuccessAnimation, redirectCountdown])

    // Iniciar verificação automática quando pixData for definido
  // useEffect(() => {
  //   if (!pixData?.transactionId) {
  //     console.log(`⏳ [${new Date().toISOString()}] Aguardando transactionId para iniciar verificação`)
  //     return
  //   }

  //   console.log(`🚀 [${new Date().toISOString()}] pixData.transactionId encontrado: ${pixData.transactionId}`)
  //   console.log(`� [${new Date().toISOString()}] Dados PIX completos:`, pixData)

  //   const interval = setInterval(() => {
  //     console.log(`⏰ [${new Date().toISOString()}] Executando verificação automática...`)
  //     checkOrderStatusEndpoint(pixData.transactionId)
  //   }, 10000) // Verificar a cada 10 segundos

  //   setVerificationInterval(interval)
  //   console.log(`✅ [${new Date().toISOString()}] Intervalo de verificação iniciado`)

  //   // Cleanup function
  //   return () => {
  //     if (interval) {
  //       clearInterval(interval)
  //       console.log(`🛑 [${new Date().toISOString()}] Limpando intervalo no cleanup`)
  //     }
  //   }
  // }, [pixData?.transactionId])

  // Monitorar mudanças no paymentStatus
  useEffect(() => {
    console.log(`📊 [${new Date().toISOString()}] Status do pagamento alterado para: ${paymentStatus}`)
    
    if (paymentStatus === 'paid') {
      console.log(`🎉 [${new Date().toISOString()}] PAGAMENTO CONFIRMADO - Mostrando tela de sucesso`)
    } else if (paymentStatus === 'failed') {
      console.log(`❌ [${new Date().toISOString()}] PAGAMENTO FALHOU`)
    } else if (paymentStatus === 'expired') {
      console.log(`⏰ [${new Date().toISOString()}] PIX EXPIRADO`)
    }
  }, [paymentStatus])

  // Monitorar mudanças no pixData
  useEffect(() => {
    console.log(`📦 [${new Date().toISOString()}] pixData alterado:`, {
      transactionId: pixData?.transactionId,
      hasTransactionId: !!pixData?.transactionId,
      startsWith_or: pixData?.transactionId?.startsWith('or_'),
      qrCode: pixData?.qrCode ? 'Presente' : 'Ausente',
      qrCodeLength: pixData?.qrCode?.length,
      qrCodePreview: pixData?.qrCode ? pixData.qrCode.substring(0, 50) + '...' : 'N/A',
      amount: pixData?.amount
    })
  }, [pixData])

  // Chamar confirmação via check-order quando a animação aparecer
  useEffect(() => {
    if (showSuccessAnimation && pixData?.transactionId && !confirmationResult && !pixData.fromUrl) {
      console.log(`🎬 [${new Date().toISOString()}] Animação de sucesso iniciada - confirmando pagamento via check-order`)
      confirmPaymentViaCheckOrder(pixData.transactionId)
    }
  }, [showSuccessAnimation, pixData?.transactionId, confirmationResult, pixData?.fromUrl])

  // Verificar se já temos dados PIX nos query parameters
  useEffect(() => {
    const qrCodeFromUrl = searchParams.get('qr_code')
    const pixKeyFromUrl = searchParams.get('pix_key')
    const paymentUrlFromUrl = searchParams.get('payment_url')
    const transactionIdFromUrl = searchParams.get('transaction_id') || searchParams.get('payment_id')
    
    console.log('🔍 Verificando TODOS os dados PIX na URL:', {
      qrCodeFromUrl: !!qrCodeFromUrl,
      pixKeyFromUrl: !!pixKeyFromUrl,
      paymentUrlFromUrl: !!paymentUrlFromUrl,
      transactionIdFromUrl: transactionIdFromUrl,
      allParams: Object.fromEntries(searchParams.entries())
    })
    
    // Se já temos dados PIX na URL, usar eles diretamente
    if (qrCodeFromUrl) {
      console.log('✅ Usando dados PIX da URL')
      
      const pixDataFromUrl = {
        qrCode: qrCodeFromUrl,
        pixKey: pixKeyFromUrl || 'tander@email.com',
        amount: parseFloat(price),
        expiresAt: new Date(Date.now() + 30 * 60 * 1000), // 30 minutos
        transactionId: transactionIdFromUrl, // Usar transactionId da URL se existir
        paymentUrl: paymentUrlFromUrl || '',
        fromUrl: true // Flag para indicar que veio da URL
      }
      
      console.log('📦 Dados PIX criados da URL:', pixDataFromUrl)
      console.log('🔍 Transaction ID final:', transactionIdFromUrl)
      console.log('🔍 Vai iniciar verificação?', !!transactionIdFromUrl)
      setPixData(pixDataFromUrl)
      setIsLoading(false)
      return
    }
    
    // Se não temos dados na URL, fazer requisição para a API
    const generatePix = async () => {
      try {
        setIsLoading(true)
        
        const requestBody = {
          plan_type: parseInt(searchParams.get('plan') || '1'),
          payment_method: 'pix'
        }
        
        console.log(`🔍 [${new Date().toISOString()}] Request body para PIX:`, requestBody)
        console.log(`🔍 [${new Date().toISOString()}] Plan type:`, requestBody.plan_type, typeof requestBody.plan_type)
        console.log(`🔍 [${new Date().toISOString()}] Payment method:`, requestBody.payment_method)
        
        // Verificar token de autenticação
        const authToken = apiService.getAuthToken()
        console.log(`🔑 [${new Date().toISOString()}] Token de autenticação:`, {
          hasToken: !!authToken,
          tokenLength: authToken?.length,
          tokenPreview: authToken ? authToken.substring(0, 20) + '...' : 'N/A'
        })
        
        // Integração real com sua API usando o apiService
        const subscriptionResult = await apiService.createSubscription({
          ...requestBody,
          payment_method: 'pix' as const
        });

        console.log(`🔍 [${new Date().toISOString()}] Resposta completa da API:`, JSON.stringify(subscriptionResult, null, 2))
        console.log(`🔍 [${new Date().toISOString()}] Success flag:`, subscriptionResult.success)
        console.log(`🔍 [${new Date().toISOString()}] Payment ID recebido:`, subscriptionResult.payment_id)

        if (subscriptionResult.success) {
          const pixDataToSet = {
            qrCode: subscriptionResult.qr_code || '00020126580014BR.GOV.BCB.PIX0136123e4567-e89b-12d3-a456-426614174000520400005303986540510.005802BR5913TANDER LTDA6008Sao Paulo62070503***6304E2CA',
            pixKey: subscriptionResult.pix_key || '',
            amount: subscriptionResult.amount ? subscriptionResult.amount / 100 : parseFloat(price), // Pagar.me retorna em centavos
            expiresAt: subscriptionResult.expires_at ? new Date(subscriptionResult.expires_at) : new Date(Date.now() + 30 * 60 * 1000), // 30 minutos
            transactionId: subscriptionResult.payment_id, // ID real da Pagar.me (ex: or_71NO304HPHW3w4pW)
            paymentUrl: subscriptionResult.qr_code_url || ''
          }
          
          console.log(`🎯 [${new Date().toISOString()}] Definindo pixData com transactionId: ${pixDataToSet.transactionId}`)
          console.log(`🔍 [${new Date().toISOString()}] Verificando se payment_id é válido:`, {
            payment_id: subscriptionResult.payment_id,
            startsWith_or: subscriptionResult.payment_id?.startsWith('or_'),
            length: subscriptionResult.payment_id?.length,
            type: typeof subscriptionResult.payment_id
          })
          console.log(`🔍 [${new Date().toISOString()}] QR Code recebido:`, {
            qr_code: subscriptionResult.qr_code,
            final_qrCode: pixDataToSet.qrCode,
            length: pixDataToSet.qrCode?.length
          })
          console.log(`📦 [${new Date().toISOString()}] Dados completos do PIX:`, pixDataToSet)
          
          setPixData(pixDataToSet)
        } else {
          throw new Error(subscriptionResult.message || 'Erro ao gerar PIX')
        }
        
      } catch (error) {
        console.error('Erro ao gerar PIX:', error)
        setError(error instanceof Error ? error.message : 'Erro ao gerar PIX. Tente novamente.')
      } finally {
        setIsLoading(false)
      }
    }

    generatePix()
  }, [price, searchParams])

  // Função para confirmar pagamento via check-order endpoint
  const confirmPaymentViaCheckOrder = async (orderId: string) => {
    try {
      setIsConfirmingPayment(true)
      console.log(`🔍 [${new Date().toISOString()}] CONFIRMANDO PAGAMENTO via check-order: ${orderId}`)
      
      const result = await checkOrderStatus(orderId)
      console.log(`✅ [${new Date().toISOString()}] Confirmação do check-order:`, result)
      
      setConfirmationResult(result)
      
      if (result.is_paid) {
        console.log(`🎉 [${new Date().toISOString()}] PAGAMENTO CONFIRMADO via check-order!`)
        
        if (result.subscription_updated) {
          console.log(`✅ [${new Date().toISOString()}] Subscription atualizada com sucesso!`)
        } else if (result.current_subscription?.active) {
          console.log(`ℹ️ [${new Date().toISOString()}] Subscription já estava ativa`)
        }
        
        // Iniciar countdown de 5 segundos
        setRedirectCountdown(5)
        
        return true
      } else {
        console.log(`❌ [${new Date().toISOString()}] Pagamento NÃO confirmado via check-order: ${result.order_status}`)
        return false
      }
    } catch (error) {
      console.error(`❌ [${new Date().toISOString()}] Erro ao confirmar pagamento via check-order:`, error)
      return false
    } finally {
      setIsConfirmingPayment(false)
    }
  }

  // Iniciar verificação automática quando pixData for definido
  useEffect(() => {
    console.log(`🔍 [${new Date().toISOString()}] useEffect de verificação executado`)
    
    if (!pixData?.transactionId) {
      console.log(`⏳ [${new Date().toISOString()}] Aguardando transactionId para iniciar verificação`)
      return
    }

    // Verificar se já tem uma verificação ativa para este transactionId
    if (currentTransactionIdRef.current === pixData.transactionId) {
      console.log(`⏸️ [${new Date().toISOString()}] Verificação já ativa para este transactionId, pulando criação de novo intervalo`)
      return
    }

    // Validar se o transactionId é um ID válido da Pagar.me (ou outro formato válido)
    if (!pixData.transactionId.startsWith('or_') && !pixData.transactionId.startsWith('pay_')) {
      console.error('❌ ID de pagamento inválido:', pixData.transactionId)
      setError('ID de pagamento inválido. Tente novamente.')
      return
    }

    // Se já foi pago, não iniciar verificação
    if (paymentStatus === 'paid') {
      console.log(`✅ [${new Date().toISOString()}] Pagamento já confirmado, não iniciando verificação`)
      return
    }

    console.log(`🚀 [${new Date().toISOString()}] Iniciando verificação automática a cada 10 segundos`)
    console.log(`🎯 [${new Date().toISOString()}] ID do pagamento: ${pixData.transactionId}`)
    
    // Marcar este transactionId como sendo verificado
    currentTransactionIdRef.current = pixData.transactionId
    
    // Função de verificação interna
    const verifyPayment = async () => {
      try {
        console.log(`🔍 [${new Date().toISOString()}] Verificando status do pedido via check-order: ${pixData.transactionId}`)
        
        const result = await checkOrderStatus(pixData.transactionId)
        console.log(`✅ [${new Date().toISOString()}] Resposta do check-order:`, result)
        
        if (result.is_paid) {
          console.log(`🎉 [${new Date().toISOString()}] PAGAMENTO CONFIRMADO via check-order!`)
          setPaymentStatus('paid')
          setShowSuccessAnimation(true)
          
          // Limpar intervalo quando pagamento for confirmado
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
          currentTransactionIdRef.current = null
        } else {
          console.log(`⏳ [${new Date().toISOString()}] Pedido ainda não pago via check-order: ${result.order_status}`)
        }
      } catch (error) {
        console.error(`❌ [${new Date().toISOString()}] Erro ao verificar status via check-order:`, error)
      }
    }
    
    // Primeira verificação imediata
    console.log(`⚡ [${new Date().toISOString()}] Executando primeira verificação imediata`)
    verifyPayment()
    
    // Configurar intervalo de 10 segundos
    const interval = setInterval(verifyPayment, 10000)
    
    intervalRef.current = interval
    console.log(`⏰ [${new Date().toISOString()}] Intervalo configurado para 10 segundos`)

    // Cleanup function
    return () => {
      console.log(`🧹 [${new Date().toISOString()}] Limpando intervalo no cleanup`)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      currentTransactionIdRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixData?.transactionId]) // Apenas transactionId como dependência
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Erro ao copiar:', error)
    }
  }

  const downloadQRCode = () => {
    if (!pixData?.qrCode) {
      alert('QR Code ainda não foi gerado')
      return
    }

    // Criar um canvas para converter o QR code SVG em imagem
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const svg = document.querySelector('.qr-code-svg') as SVGElement
    
    if (svg && ctx) {
      const svgData = new XMLSerializer().serializeToString(svg)
      const img = new Image()
      
      img.onload = () => {
        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0)
        
        // Criar link de download
        const link = document.createElement('a')
        link.download = 'pix-qrcode.png'
        link.href = canvas.toDataURL('image/png')
        link.click()
      }
      
      img.src = 'data:image/svg+xml;base64,' + btoa(svgData)
    } else {
      // Fallback: copiar o código PIX
      copyToClipboard(pixData.qrCode)
      alert('QR Code copiado para a área de transferência!')
    }
  }

  console.log('🎯 Verificando condições de renderização:', {
    isLoading,
    error,
    showSuccessAnimation,
    pixData: !!pixData
  })

  if (isLoading) {
    console.log('⏳ Renderizando tela de loading...')
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="flex justify-center mb-6">
            <img 
              src={logoTander} 
              alt="TANDER SMART" 
              className="h-12 w-auto"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Gerando PIX...</h1>
          <p className="text-xl text-gray-600 mb-8">
            Aguarde enquanto preparamos seu pagamento
          </p>
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </motion.div>
      </div>
    )
  }

  if (error) {
    console.log('❌ Renderizando tela de erro:', error)
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Erro ao Gerar PIX</h1>
          <p className="text-xl text-gray-600 mb-8">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-primary-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-primary-600 transition-colors duration-200"
          >
            Tentar Novamente
          </button>
        </motion.div>
      </div>
    )
  }

  // Animação de sucesso
  if (showSuccessAnimation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ 
            type: "spring", 
            stiffness: 260, 
            damping: 20,
            duration: 0.8
          }}
          className="text-center"
        >
          {/* Círculo de sucesso animado */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ 
              type: "spring", 
              stiffness: 200, 
              damping: 15,
              delay: 0.2
            }}
            className="w-32 h-32 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: "spring" }}
            >
              <CheckCircle className="w-16 h-16 text-white" />
            </motion.div>
          </motion.div>

          {/* Confetes animados */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="absolute inset-0 pointer-events-none"
          >
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ 
                  x: Math.random() * window.innerWidth, 
                  y: -100,
                  opacity: 0 
                }}
                animate={{ 
                  y: window.innerHeight + 100,
                  opacity: [0, 1, 0],
                  rotate: Math.random() * 360
                }}
                transition={{ 
                  duration: 3 + Math.random() * 2,
                  delay: 0.8 + Math.random() * 0.5,
                  repeat: Infinity,
                  repeatDelay: 2
                }}
                className="absolute w-2 h-2 bg-yellow-400 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              />
            ))}
          </motion.div>

          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            Pagamento Confirmado! 🎉
          </motion.h1>

          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="text-xl text-gray-600 mb-4"
          >
            Seu pagamento foi processado com sucesso!
          </motion.p>

          {/* Status da confirmação */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.0 }}
            className="mb-6"
          >
            {isConfirmingPayment ? (
              <div className="flex items-center justify-center space-x-2 text-blue-600">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm font-medium">Confirmando pagamento...</span>
              </div>
            ) : confirmationResult ? (
              <div className="text-center">
                {confirmationResult.subscription_updated ? (
                  <div className="text-green-600 text-sm font-medium">
                    ✅ Subscription ativada com sucesso!
                  </div>
                ) : confirmationResult.current_subscription?.active ? (
                  <div className="text-blue-600 text-sm font-medium">
                    ℹ️ Subscription já estava ativa
                  </div>
                ) : (
                  <div className="text-gray-600 text-sm font-medium">
                    ✅ Pagamento confirmado
                  </div>
                )}
              </div>
            ) : (
              <div className="text-gray-500 text-sm">
                Aguardando confirmação...
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="text-sm text-gray-500"
          >
            {confirmationResult ? (
              redirectCountdown > 0 ? (
                <div className="text-center">
                  <div className="text-lg font-semibold text-blue-600 mb-2">
                    Redirecionando em {redirectCountdown} segundos...
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`bg-blue-600 h-2 rounded-full transition-all duration-1000 ease-linear ${
                        redirectCountdown === 5 ? 'w-1/5' :
                        redirectCountdown === 4 ? 'w-2/5' :
                        redirectCountdown === 3 ? 'w-3/5' :
                        redirectCountdown === 2 ? 'w-4/5' :
                        redirectCountdown === 1 ? 'w-full' : 'w-0'
                      }`}
                    ></div>
                  </div>
                </div>
              ) : (
                'Redirecionando...'
              )
            ) : (
              'Aguarde a confirmação...'
            )}
          </motion.div>

          {/* Loading spinner */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="mt-6"
          >
            <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </motion.div>
        </motion.div>
      </div>
    )
  }

  console.log('🎯 Renderizando página principal do PIX')
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white shadow-sm border-b border-gray-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-gray-600 hover:text-primary-500 transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Voltar</span>
            </button>
            
            <div className="flex items-center space-x-3">
              <img 
                src={logoTander} 
                alt="TANDER SMART" 
                className="h-8 w-auto"
              />
              <span className="text-lg font-semibold text-gray-900">Pagamento PIX</span>
            </div>
          </div>
        </div>
      </motion.header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* QR Code e Informações */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                  <img 
                    src={logoTander} 
                    alt="TANDER SMART" 
                    className="h-12 w-auto"
                  />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Pague com PIX
                </h1>
                <p className="text-gray-600">
                  Escaneie o QR Code ou copie a chave PIX
                </p>
              </div>

              {/* Informações do Plano */}
              <div className="bg-accent-50 rounded-xl p-4 mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-accent-900">{planName}</h3>
                    <p className="text-sm text-accent-700">Assinatura mensal</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-accent-900">R$ {price}</p>
                    <p className="text-sm text-accent-700">/mês</p>
                  </div>
                </div>
              </div>

              {/* QR Code */}
              <div className="text-center mb-8">
                <div className="bg-gray-100 rounded-2xl p-8 mb-4">
                  <div className="w-48 h-48 bg-white rounded-xl mx-auto flex items-center justify-center p-4">
                    {pixData?.qrCode ? (
                      <QRCodeSVG
                        value={pixData.qrCode}
                        size={160}
                        level="M"
                        includeMargin={true}
                        className="w-full h-full qr-code-svg"
                      />
                    ) : (
                      <QrCode className="w-32 h-32 text-gray-400" />
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Escaneie com o app do seu banco
                </p>
                
                <div className="flex space-x-3">
                  <motion.button
                    onClick={downloadQRCode}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <Download className="w-4 h-4" />
                    <span>Baixar</span>
                  </motion.button>
                  
                  <motion.button
                    onClick={() => copyToClipboard(pixData?.qrCode || '')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 bg-primary-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-600 transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <Copy className="w-4 h-4" />
                    <span>{copied ? 'Copiado!' : 'Copiar'}</span>
                  </motion.button>
                </div>
              </div>

              {/* Chave PIX */}
              {/* <div className="mb-8">
              
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={pixData?.pixKey || ''}
                    readOnly
                    className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700"
                  />
                  <motion.button
                    onClick={() => copyToClipboard(pixData?.pixKey || '')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-4 py-3 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors duration-200"
                  >
                    <Copy className="w-5 h-5" />
                  </motion.button>
                </div>
              </div> */}

              {/* Timer */}
              <div className="text-center">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Clock className="w-5 h-5 text-accent-500" />
                  <span className="text-sm font-medium text-gray-700">Expira em:</span>
                </div>
                <div className="text-2xl font-bold text-accent-600">
                  {formatTime(countdown)}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Após o pagamento, aguarde a confirmação automática
                </p>
              </div>

              {/* Status do Pagamento */}
              <div className="text-center mt-6">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <motion.div
                    animate={{ 
                      scale: paymentStatus === 'pending' ? [1, 1.1, 1] : 1,
                      opacity: paymentStatus === 'pending' ? [0.7, 1, 0.7] : 1
                    }}
                    transition={{ 
                      duration: 2, 
                      repeat: paymentStatus === 'pending' ? Infinity : 0 
                    }}
                    className={`w-3 h-3 rounded-full ${
                      paymentStatus === 'pending' ? 'bg-yellow-500' :
                      paymentStatus === 'paid' ? 'bg-green-500' :
                      paymentStatus === 'failed' ? 'bg-red-500' :
                      'bg-gray-500'
                    }`}
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {paymentStatus === 'pending' ? 'Aguardando pagamento...' :
                     paymentStatus === 'paid' ? 'Pagamento confirmado!' :
                     paymentStatus === 'failed' ? 'Pagamento falhou' :
                     paymentStatus === 'expired' ? 'PIX expirado' :
                     'Verificando...'}
                  </span>
                </div>
                {paymentStatus === 'pending' && (
                  <p className="text-xs text-gray-500">
                    Verificando a cada 10 segundos...
                  </p>
                )}
              </div>
            </div>
          </motion.div>

          {/* Instruções e Informações */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-6"
          >
            {/* Como Pagar */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Como Pagar com PIX</h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary-600">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Abra o app do seu banco</h4>
                    <p className="text-sm text-gray-600">Ou acesse o internet banking</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary-600">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Escolha PIX</h4>
                    <p className="text-sm text-gray-600">Selecione a opção PIX no menu</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary-600">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Escaneie o QR Code</h4>
                    <p className="text-sm text-gray-600">Ou cole a chave PIX manualmente</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary-600">4</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Confirme o pagamento</h4>
                    <p className="text-sm text-gray-600">Verifique os dados e confirme</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Vantagens do PIX */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Vantagens do PIX</h3>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Pagamento instantâneo</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Sem taxas adicionais</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Disponível 24/7</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Seguro e confiável</span>
                </div>
              </div>
            </div>

            {/* Bancos Suportados */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Bancos Suportados</h3>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                  <Building className="w-5 h-5 text-primary-500" />
                  <span className="text-sm font-medium text-gray-700">Todos os bancos</span>
                </div>
                <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                  <Smartphone className="w-5 h-5 text-accent-500" />
                  <span className="text-sm font-medium text-gray-700">Apps móveis</span>
                </div>
              </div>
            </div>

            {/* Suporte */}
            <div className="bg-gradient-to-br from-accent-500 to-primary-500 rounded-2xl p-6 text-white">
              <h3 className="text-lg font-semibold mb-2">Precisa de Ajuda?</h3>
              <p className="text-accent-100 mb-4">
                Nossa equipe está disponível 24/7 para ajudar com qualquer dúvida
              </p>
              <button className="bg-white text-accent-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-200">
                Falar com Suporte
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default PixPaymentPage
